<template src="./template.html"></template>
<style lang="scss" src="./styles.scss" scoped></style>

<script setup>
import { useStore } from "vuex"
import { useI18n } from "vue-i18n"
import {computed, onMounted, ref} from 'vue';
import { showSnackbar } from "@/helper"

import Aside from './Aside/index.vue'
import Content from './Content/index.vue'

import { paymentManager } from "@/composables/usePayment"
import { useUser } from "@/app/Checkout/composables/useUser"
import { useAuth } from "@/app/Checkout/composables/useAuth"
import { useCheckout } from "@/app/Checkout/composables/useCheckout"
import { useAddresses } from "@/app/Checkout/composables/useAddresses"
import {trackScreenView} from '../../../../helper';
import {useBreakpoint} from '../../composables/useBreakpoint';
import { scrollToError } from '@/helper'

const { t } = useI18n()
const store = useStore()
const contentRef = ref(null)
const { addresses } = useUser()
const { isLoggedIn } = useAuth()
const { shippingAddress, validateAddresses } = useCheckout()
const { errors, setErrors, createAddress, isBillingAddressSame } = useAddresses()
const { isMobile } = useBreakpoint()
const checkout = computed(() => store.state.checkout.checkout)
const mode = computed(() => (isLoggedIn?.value && addresses?.value?.length) ? 'list' : 'form')
const setAddressValidationErrors = (errors) => {
  if (errors.delivery_address) {
    setErrors({ errors: { generic: errors.delivery_address }, type: 'delivery_address' })
  }
  if (errors.invoice_address) {
    setErrors({ errors: { generic: errors.invoice_address }, type: 'invoice_address' })
  }
}


const validateAndProceedToPayment = async () => {
  try {
    const validated = await validateAddresses()

    if (validated?.errors) {
      setAddressValidationErrors(validated?.errors)
    } else {
      await paymentManager.init()
    }
  } catch (error) {
    // validateAddresses may reject on 400 with error data
    if (error?.errors) {
      setAddressValidationErrors(error.errors)
    } else {
      // Fallback: show a generic error
      showSnackbar('error', 'iconfont-info', t('messages.checkout.purchase.invalid_address'))
    }
  }
}

const goBack = () => {
  const url = sessionStorage.getItem("pageUrl")

  if (url) {
    window.location.href = url
  } else {
    window.history.back()
  }
}

const handleSubmit = () => {
  paymentManager.preInit()
  handleSubmitAfter()
}

const handleSubmitAfter = async () => {
  if (!contentRef.value) return
  if (mode.value === 'list') {
    if (shippingAddress.value?.is_invalid) {
      showSnackbar('error', 'iconfont-info', t('messages.checkout.purchase.invalid_address'))
    } else {
      await validateAndProceedToPayment()
    }
  } else {
    const { id, ...shippingAddress } = contentRef.value?.shippingFormData()
    const extendedShippingPayload = {
      ...shippingAddress,
      is_invoice: false,
      is_delivery: true,
      is_same: isBillingAddressSame.value,
    }
    try {
      const response = await createAddress(extendedShippingPayload)

      if (response.redirect) {
        window.location.href = response.url

        return
      }

      try {
        if (!isBillingAddressSame.value) {
          const { id, ...invoiceAddress } = contentRef.value?.invoiceFormData()
          const extendedInvoicePayload = {
            ...invoiceAddress,
            is_invoice: true,
            is_delivery: false,
            is_same: false,
          }
          await createAddress(extendedInvoicePayload)
        }

        await validateAndProceedToPayment()
      } catch(response) {
        setErrors({ errors: response.errors, type: 'invoice_address' })
        scrollToError()
      }
    } catch(response) {
      setErrors({ errors: response.errors, type: 'delivery_address' })
      scrollToError()
    }
  }
}

onMounted(() => {
  if (!isMobile.value) {
    trackScreenView('Checkout', {}, 'purchase')
  }
})
</script>
